import type { CurrencyCode, LocalPrintJob, Order, PrintJobKind } from "@shared/contracts";
import { bridgeLifecycleStatus, getPrinterFormatProfile, nextBridgeDeviceTransition, type BridgeLifecycleStatus, type NamedPrinterDeviceProfile, type PaperWidth, validateNamedPrinterDeviceProfile } from "./hardware";
import { translate } from "./i18n";

export type ReceiptPrintJob = {
  jobId: string;
  orderId: string;
  createdAt: string;
  lines: Array<{ name: string; quantity: number; totalMinor: number; options?: string[]; note?: string }>;
  totalMinor: number;
  currency: CurrencyCode;
  paperWidth: PaperWidth;
  kind?: PrintJobKind;
  orderNo?: string;
  orderNote?: string;
  discountMinor?: number;
  discountType?: string;
  discountReason?: string;
  language?: "en" | "zh-Hant" | "zh-Hans";
};

export type PrintResult = {
  jobId: string;
  transport: "web-serial" | "local-bridge" | "memory";
  printedAt: string;
};

export type PrintQueueRepository = {
  listPrintJobs(): Promise<LocalPrintJob[]>;
  listOrders(): Promise<Order[]>;
  updatePrintJob(job: LocalPrintJob): Promise<void>;
};

export type PrintQueueResult = {
  attempted: number;
  printed: number;
  failed: number;
};

export interface PrintAdapter {
  readonly transport: PrintResult["transport"];
  isAvailable(): Promise<boolean>;
  print(job: ReceiptPrintJob): Promise<PrintResult>;
}

export type PrintAdapterSet = PrintAdapter | Partial<Record<PrintJobKind, PrintAdapter>>;

function adapterForJob(adapterSet: PrintAdapterSet, job: LocalPrintJob): PrintAdapter | undefined {
  if ("isAvailable" in adapterSet) return adapterSet;
  return adapterSet[job.kind] ?? adapterSet[job.kind === "kitchen" ? "receipt" : "kitchen"];
}

export async function processPendingPrintJobs(
  repository: PrintQueueRepository,
  adapter: PrintAdapterSet,
  now = new Date().toISOString(),
): Promise<PrintQueueResult> {
  const jobs = (await repository.listPrintJobs())
    .filter((job) => job.status === "pending" || job.status === "failed")
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  if (jobs.length === 0) return { attempted: 0, printed: 0, failed: 0 };
  const orders = new Map((await repository.listOrders()).map((order) => [order.id, order]));
  let printed = 0;
  let failed = 0;
  for (const job of jobs) {
    const adapterForCurrentJob = adapterForJob(adapter, job);
    let available = false;
    try { available = adapterForCurrentJob ? await adapterForCurrentJob.isAvailable() : false; } catch { available = false; }
    const order = orders.get(job.orderId);
    const attemptCount = job.attemptCount + 1;
    const attempt = { ...job, status: "pending" as const, attemptCount, lastError: undefined };
    await repository.updatePrintJob(attempt);
    try {
      if (!adapterForCurrentJob) throw new Error(`No print adapter is configured for ${job.kind}`);
      if (!available) throw new Error(`${adapterForCurrentJob.transport} transport is unavailable`);
      if (!order) throw new Error(`Order ${job.orderId} is unavailable for print job`);
      const printJob = job.kind === "kitchen"
        ? kitchenJobFromOrder(order, job.id, job.paperWidth, job.language)
        : receiptJobFromOrder(order, job.id, job.paperWidth, job.language);
      await adapterForCurrentJob!.print(printJob);
      await repository.updatePrintJob({ ...attempt, status: "printed", lastError: undefined });
      printed += 1;
    } catch (error) {
      await repository.updatePrintJob({ ...attempt, status: "failed", lastError: error instanceof Error ? error.message : "Print failed" });
      failed += 1;
    }
  }
  return { attempted: jobs.length, printed, failed };
}

export function receiptJobFromOrder(order: Order, jobId: string, paperWidth: PaperWidth = 58, language: ReceiptPrintJob["language"] = "en"): ReceiptPrintJob {
  return {
    jobId,
    orderId: order.id,
    createdAt: order.createdAt,
    currency: order.currency,
    paperWidth,
    kind: "receipt",
    orderNo: order.orderNo,
    lines: order.lines.map((line) => ({
      name: line.name,
      quantity: line.quantity,
      totalMinor: (line.unitPriceMinor + (line.options ?? []).reduce((sum, option) => sum + option.priceMinor, 0)) * line.quantity,
      options: line.options?.map((option) => option.name),
      note: line.note,
    })),
    totalMinor: order.totalMinor,
    orderNote: order.note,
    discountMinor: order.discountMinor,
    discountType: order.discountType,
    discountReason: order.discountReason,
    language,
  };
}

export function kitchenJobFromOrder(order: Order, jobId: string, paperWidth: PaperWidth = 58, language: ReceiptPrintJob["language"] = "en"): ReceiptPrintJob {
  return {
    ...receiptJobFromOrder(order, jobId, paperWidth, language),
    kind: "kitchen",
    totalMinor: 0,
  };
}

function formatMinor(minor: number): string {
  const sign = minor < 0 ? "-" : "";
  const absolute = Math.abs(minor);
  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`;
}

function wrapText(value: string, width: number): string[] {
  const normalized = value.trim();
  if (!normalized) return [];
  const lines: string[] = [];
  for (let offset = 0; offset < normalized.length; offset += width) lines.push(normalized.slice(offset, offset + width));
  return lines;
}

export function encodeReceipt(job: ReceiptPrintJob): Uint8Array {
  const profile = getPrinterFormatProfile(job.paperWidth);
  const language = job.language ?? "en";
  const label = (key: string, fallback: string): string => translate(language, key, fallback);
  const itemLines = job.lines.flatMap((line) => [
    `${line.quantity} x ${line.name.slice(0, profile.printWidth)}${job.kind === "kitchen" ? "" : `  ${formatMinor(line.totalMinor)}`}`,
    ...(line.options ?? []).flatMap((option) => [`  + ${wrapText(option, Math.max(1, profile.printWidth - 4))[0] ?? ""}`, ...wrapText(option, Math.max(1, profile.printWidth - 4)).slice(1).map((part) => `    ${part}`)]),
    ...(line.note ? [`  ${label("receipt.note", "NOTE")}:`, ...wrapText(line.note, profile.printWidth - 2).map((part) => `    ${part}`)] : []),
  ]);
  const text = [
    "POSNext",
    `${job.kind === "kitchen" ? label("receipt.kitchen", "KITCHEN") : label("receipt.receipt", "RECEIPT")} ${job.orderNo ?? job.orderId}`,
    new Date(job.createdAt).toLocaleString("en-GB"),
    "-".repeat(profile.printWidth),
    ...(job.orderNote ? [`${label("receipt.orderNote", "ORDER NOTE")}:`, ...wrapText(job.orderNote, profile.printWidth)] : []),
    ...itemLines,
    ...(job.kind === "kitchen" ? [] : [
      "-".repeat(profile.printWidth),
      ...(job.discountMinor ? [`${label("receipt.discount", "DISCOUNT")} ${job.currency} ${formatMinor(job.discountMinor)}`, ...(job.discountType && job.discountType !== "none" ? [`${label("receipt.discountType", "DISCOUNT TYPE")}: ${job.discountType}`] : []), ...(job.discountReason ? [`${label("receipt.discountReason", "DISCOUNT REASON")}:`, ...wrapText(job.discountReason, profile.printWidth)] : [])] : []),
      `${label("receipt.total", "TOTAL")} ${job.currency} ${formatMinor(job.totalMinor)}`,
    ]),
    "",
    label("receipt.thankYou", "Thank you"),
    "",
    "",
  ].join("\n");
  const payload = new TextEncoder().encode(text);
  const prefix = new Uint8Array([0x1b, 0x40]);
  const cut = new Uint8Array([0x1d, 0x56, 0x00]);
  const result = new Uint8Array(prefix.length + payload.length + cut.length);
  result.set(prefix);
  result.set(payload, prefix.length);
  result.set(cut, prefix.length + payload.length);
  return result;
}

export function encodeCashDrawerPulse(): Uint8Array {
  return new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa]);
}

export function encodePartialCut(feed = 60): Uint8Array {
  if (!Number.isInteger(feed) || feed < 0 || feed > 255) throw new Error("Printer cut feed must be between 0 and 255");
  return new Uint8Array([0x1d, 0x56, 0x42, feed]);
}

export type SerialWritable = {
  getWriter(): {
    write(data: Uint8Array): Promise<void>;
    releaseLock(): void;
  };
};

export type SerialPortLike = {
  open(options: { baudRate: number }): Promise<void>;
  close?(): Promise<void>;
  writable: SerialWritable | null;
};

export type SerialNavigator = {
  serial?: {
    requestPort(): Promise<SerialPortLike>;
  };
};

export class WebSerialPrintAdapter implements PrintAdapter {
  readonly transport = "web-serial" as const;

  constructor(private readonly browserNavigator: SerialNavigator = globalThis.navigator as SerialNavigator) {}

  isAvailable(): Promise<boolean> {
    return Promise.resolve(Boolean(this.browserNavigator.serial));
  }

  async print(job: ReceiptPrintJob): Promise<PrintResult> {
    const serial = this.browserNavigator.serial;
    if (!serial) throw new Error("Web Serial is not available in this browser");
    const port = await serial.requestPort();
    await port.open({ baudRate: 9600 });
    const writable = port.writable;
    if (!writable) {
      await port.close?.();
      throw new Error("Selected serial printer is not writable");
    }
    const writer = writable.getWriter();
    try {
      await writer.write(encodeReceipt(job));
    } finally {
      writer.releaseLock();
      await port.close?.();
    }
    return { jobId: job.jobId, transport: this.transport, printedAt: new Date().toISOString() };
  }
}

export class LocalPrintBridgeAdapter implements PrintAdapter {
  readonly transport = "local-bridge" as const;

  constructor(
    private readonly endpoint = "http://127.0.0.1:17890",
    private readonly fetcher: typeof fetch = fetch,
  ) {}

  async isAvailable(): Promise<boolean> {
    try {
      const response = await this.fetcher(`${this.endpoint}/health`);
      return response.ok;
    } catch {
      return false;
    }
  }

  async print(job: ReceiptPrintJob): Promise<PrintResult> {
    const response = await this.fetcher(`${this.endpoint}/print`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(job),
    });
    if (!response.ok) throw new Error(`Local print bridge failed (${response.status})`);
    return { jobId: job.jobId, transport: this.transport, printedAt: new Date().toISOString() };
  }

  async openCashDrawer(): Promise<void> {
    const response = await this.fetcher(`${this.endpoint}/drawer/open`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ command: Array.from(encodeCashDrawerPulse()) }),
    });
    if (!response.ok) throw new Error(`Local print bridge drawer command failed (${response.status})`);
  }

  async testPrint(paperWidth: PaperWidth): Promise<void> {
    const response = await this.fetcher(`${this.endpoint}/test-print`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ paperWidth, command: Array.from(encodePartialCut()) }),
    });
    if (!response.ok) throw new Error(`Local print bridge test print failed (${response.status})`);
  }

  async status(minimumVersion = "1.0.0"): Promise<BridgeLifecycleStatus> {
    try {
      const response = await this.fetcher(`${this.endpoint}/version`);
      if (!response.ok) return bridgeLifecycleStatus({ installed: false, paired: false, connected: false, minimumVersion, lastError: `Bridge version check failed (${response.status})` });
      const body = await response.json() as { version?: string; paired?: boolean; connected?: boolean };
      return bridgeLifecycleStatus({ installed: true, version: body.version ?? null, paired: Boolean(body.paired), connected: Boolean(body.connected), minimumVersion });
    } catch (error) {
      return bridgeLifecycleStatus({ installed: false, paired: false, connected: false, minimumVersion, lastError: error instanceof Error ? error.message : "Bridge version check failed" });
    }
  }

  async install(minimumVersion = "1.0.0"): Promise<BridgeLifecycleStatus> {
    try {
      const response = await this.fetcher(`${this.endpoint}/install`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ minimumVersion }) });
      if (!response.ok) return bridgeLifecycleStatus({ installed: false, paired: false, connected: false, minimumVersion, lastError: `Bridge installation failed (${response.status})` });
      return await this.status(minimumVersion);
    } catch (error) {
      return bridgeLifecycleStatus({ installed: false, paired: false, connected: false, minimumVersion, lastError: error instanceof Error ? error.message : "Bridge installation failed" });
    }
  }

  async pair(profile: NamedPrinterDeviceProfile): Promise<BridgeLifecycleStatus> {
    validateNamedPrinterDeviceProfile(profile);
    const current = await this.status();
    try {
      const response = await this.fetcher(`${this.endpoint}/pair`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ profileId: profile.id, deviceAddress: profile.deviceAddress, transport: profile.transport }) });
      return nextBridgeDeviceTransition(current, "pair", { ok: response.ok, error: response.ok ? undefined : `Printer pairing failed (${response.status})` });
    } catch (error) {
      return nextBridgeDeviceTransition(current, "pair", { ok: false, error: error instanceof Error ? error.message : "Printer pairing failed" });
    }
  }

  async reconnect(profile: NamedPrinterDeviceProfile): Promise<BridgeLifecycleStatus> {
    validateNamedPrinterDeviceProfile(profile);
    const current = await this.status();
    try {
      const response = await this.fetcher(`${this.endpoint}/reconnect`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ profileId: profile.id, deviceAddress: profile.deviceAddress, transport: profile.transport }) });
      return nextBridgeDeviceTransition(current, "reconnect", { ok: response.ok, error: response.ok ? undefined : `Printer reconnect failed (${response.status})` });
    } catch (error) {
      return nextBridgeDeviceTransition(current, "reconnect", { ok: false, error: error instanceof Error ? error.message : "Printer reconnect failed" });
    }
  }
}

export class MemoryPrintAdapter implements PrintAdapter {
  readonly transport = "memory" as const;
  readonly jobs: ReceiptPrintJob[] = [];

  isAvailable(): Promise<boolean> {
    return Promise.resolve(true);
  }

  async print(job: ReceiptPrintJob): Promise<PrintResult> {
    this.jobs.push(job);
    return { jobId: job.jobId, transport: this.transport, printedAt: new Date().toISOString() };
  }
}
